import React, { useState } from "react";
import { useFormik } from "formik";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaFilter, FaUndo } from "react-icons/fa";
import AppButton from "../../../../shared/AppButton";
import CustomFilterInput from "../../../../shared/CustomFilterInput";

function Filter({
  refetch,
  setPageNo,
  setFromDate,
  setToDate,
  setLoading,
  setRefetchFilterVariables,
  pageNo,
  limit,
  setSubmitClaimData,
  setTotalPages,
  setTotalSubmits,
}) {
  const { t } = useTranslation();
  const [payers, setPayers] = useState([]);
  const [isApplying, setIsApplying] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const currentDate = new Date();
  const navigate = useNavigate();

  const buildObj = (values, overrides = {}) => ({
    from_date: values.fromDate,
    to_date: values.toDate,
    payer_id: overrides.payerId ?? values.payerId,
    batch_id: overrides.batchID ?? values.batchID,
    tp_ref_no: overrides.tpRefNo ?? values.tpRefNo,
    status: "Ready for submission",
    limit,
    page: pageNo,
  });

  const stripEmpty = (obj) =>
    Object.keys(obj).reduce((acc, key) => {
      const value = obj[key];
      if (value !== "" && value !== null) acc[key] = value;
      return acc;
    }, {});

  const addData = async (values) => {
    setPageNo(1);
    setFromDate(values.fromDate);
    setToDate(values.toDate);
    const obj = buildObj(values);
    setLoading(true);
    setIsApplying(true);
    await refetch({ input: stripEmpty(obj) }).then((res) => {
      if (res?.data?.listPayerClaims?.data) {
        setSubmitClaimData(res.data.listPayerClaims.data);
        setTotalPages(res.data.listPayerClaims.pages);
        setTotalSubmits(res.data.listPayerClaims.total);
      }
    });
    setLoading(false);
    setIsApplying(false);
    setRefetchFilterVariables(stripEmpty(obj));
  };

  const handleResetForm = async (values) => {
    setPageNo(1);
    setFromDate(values.fromDate);
    setToDate(values.toDate);
    const obj = buildObj(values, { payerId: "", batchID: "", tpRefNo: "" });
    setLoading(true);
    setIsResetting(true);
    await refetch({ input: stripEmpty(obj) }).then((res) => {
      if (res?.data?.listPayerClaims?.data) {
        setSubmitClaimData(res.data.listPayerClaims.data);
        setTotalPages(res.data.listPayerClaims.pages);
        setTotalSubmits(res.data.listPayerClaims.total);
      }
    });
    setLoading(false);
    setIsResetting(false);
    setRefetchFilterVariables(stripEmpty(obj));
  };

  React.useEffect(() => {
    const payersData = JSON.parse(localStorage.getItem("payersData"));
    if (payersData && payersData.length > 0) {
      setPayers(payersData);
    } else {
      navigate("/dashboard");
    }
  }, []);

  const { values, handleChange, handleBlur, handleSubmit, resetForm } =
    useFormik({
      initialValues: {
        fromDate: `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-01`,
        toDate: `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0,
        ).getDate()}`,
        payerId: "",
        batchID: "",
        tpRefNo: "",
      },
      onSubmit: (values) => addData(values),
      onReset: (values) => handleResetForm(values),
    });

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-end justify-end gap-2 w-full">
        <CustomFilterInput
          label={t("From Date")}
          type="date"
          name="fromDate"
          id="fromDate"
          value={values.fromDate}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("From Date")}
          className="w-full md:w-[180px]"
        />
        <CustomFilterInput
          label={t("To Date")}
          type="date"
          name="toDate"
          id="toDate"
          value={values.toDate}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("To Date")}
          className="w-full md:w-[180px]"
        />
        <CustomFilterInput
          className="w-full md:w-[180px]"
          label={t("Select Payer")}
          name="payerId"
          id="payerId"
          value={values.payerId}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Select Payer")}
          isSelect
          renderOptions={() =>
            payers.map((category) => (
              <optgroup
                key={category.id}
                label={
                  category?.name_en
                    ? `${category?.name_en} (${category?.license_id})`
                    : ""
                }
              >
                {category?.payers?.map((insurance, idx) => (
                  <option key={`${insurance.id}-${idx}`} value={insurance.id}>
                    {insurance.name_en} ({insurance?.license_id})
                  </option>
                ))}
              </optgroup>
            ))
          }
        />
        <CustomFilterInput
          label={t("Claim Reference")}
          type="text"
          name="tpRefNo"
          id="tpRefNo"
          value={values.tpRefNo}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Claim Reference")}
          className="w-full md:w-[180px]"
        />

        <div className="flex justify-end gap-2">
          <AppButton
            variant="reset"
            onClick={resetForm}
            startIcon={<FaUndo className="!text-[12px]" />}
            isLoading={isResetting}
            loadingText=""
          />
          <AppButton
            onClick={handleSubmit}
            startIcon={<FaFilter className="!text-[12px]" />}
            isLoading={isApplying}
            loadingText=""
          />
        </div>
      </div>
    </div>
  );
}

export default React.memo(Filter);
